/** @module Auth0ChangePasswordPageHelper */
/* eslint-disable import/extensions */
import { Auth0PageBase } from './auth0-page-template.mst.mjs';
import Auth0WhiteLabelAssetHelper from '../utils/auth0-white-label-helper.mst.mjs';
import Auth0Localizer from '../../utils/auth0-localization-helper.mjs';
import Auth0UtilsBrowser from '../../utils/auth0-utils-browser.mjs';
import ChangePasswordHtml from '../../../public/changePassword.html.mst';
import InvokeChangePasswordHtml from '../../../public/invokeChangePassword.html.mst';
import AutoInvokeChangePasswordHtml from '../../../public/autoInvokeChangePassword.html.mst';
import Auth0HttpHelper from '../../utils/auth0-http-helper.mjs';
import Auth0Cdn from '../utils/auth0-cdn-resource-resolver.mst.mjs';
import Auth0Constants from '../../utils/auth0-constants.mjs';
import logger from '../../utils/logging/auth0-logger.mjs';
import Auth0UserSessionHelper from '../../utils/auth0-session-helper.mjs';
import AuthAnalytics from '../../utils/auth-analytics.mjs';
import {
  STEP_START_CHANGE_PASSWORD_PAGE,
  STEP_CHANGE_PASSWORD_PAGE,
  EVENT_CHANGE_PASSWORD_FLOW_STARTED,
  EVENT_CHANGE_PASSWORD_BUTTON_CLICKED,
  EVENT_CHANGE_PASSWORD_SUCCESS,
  EVENT_CHANGE_PASSWORD_FAILED,
  EVENT_CHANGE_PASSWORD_NEW_PASSWORD_ENTERED,
  EVENT_CHANGE_PASSWORD_CONFIRMATION_ENTERED,
  EVENT_CHANGE_PASSWORD_EMAIL_ENTERED,
  EVENT_CHANGE_PASSWORD_CANCEL,
} from '../../utils/auth-analytics-constants.mjs';

const PASSWORD_REQUIREMENTS_MAP = {
  excellent: {
    hasMinPasswordLength: true,
    hasUpperAndLowerCaseAndNumberRequirement: true,
    hasSpecialCharacterRequirement: true,
    hasNoMoreThanTwoIdenticalCharsInARowRequirement: true,
  },
  good: {
    hasMinPasswordLength: true,
    hasUpperAndLowerCaseAndNumberRequirement: true,
    hasSpecialCharacterRequirement: true,
    hasNoMoreThanTwoIdenticalCharsInARowRequirement: false,
  },
  fair: {
    hasMinPasswordLength: true,
    hasUpperAndLowerCaseAndNumberRequirement: true,
    hasSpecialCharacterRequirement: false,
    hasNoMoreThanTwoIdenticalCharsInARowRequirement: false,
  },
  low: {
    hasMinPasswordLength: true,
    hasUpperAndLowerCaseAndNumberRequirement: false,
    hasSpecialCharacterRequirement: false,
    hasNoMoreThanTwoIdenticalCharsInARowRequirement: false,
  },
  none: {
    hasMinPasswordLength: false,
    hasUpperAndLowerCaseAndNumberRequirement: false,
    hasSpecialCharacterRequirement: false,
    hasNoMoreThanTwoIdenticalCharsInARowRequirement: false,
  },
};

const AUTH0_CONTINUE_STATE_KEY = 'auth0_continue_state';
const SPECIAL_CHARACTERS = '!@#$%^&*';

const COLOR_VALID_FIELD = '#00a900';
const COLOR_INVALID_FIELD = '#F00';

// determine of we invoke password flow via auth0 or via shipstation (solves user email sync issues)

class Auth0PasswordPolicyChecklist {
  constructor(options) {
    this._name = options.passwordPolicyName;
    this._currentPassword = '';
    this._passwordPolicy = PASSWORD_REQUIREMENTS_MAP[this._name] || PASSWORD_REQUIREMENTS_MAP.none;
    this._passwordMinLength = options.passwordMinLength;
    this._checklistItems = [];

    this._initChecklistItems();
    console.log('construct end');
  }

  get _passwordValidIconUrl() {
    return `${Auth0Cdn.AUTH0_IMAGES_URL}/${Auth0Constants.PASSWORD_VALID_ICON}`;
  }

  get _passwordInvalidIconUrl() {
    return `${Auth0Cdn.AUTH0_IMAGES_URL}/${Auth0Constants.PASSWORD_INVALID_ICON}`;
  }

  _initChecklistItems = () => {
    if (this._passwordPolicy.hasMinPasswordLength && this._passwordMinLength) {
      this._checklistItems.push({
        id: 'hasMinPasswordLength',
        displayText: Auth0Localizer.getLocalizedText('password-policy-length', { minLength: this._passwordMinLength }),
        passwordMeetsRequirement: false,
        test: (newPassword) => {
          return newPassword?.length >= this._passwordMinLength;
        },
      });
    }
    if (this._passwordPolicy.hasUpperAndLowerCaseAndNumberRequirement) {
      this._checklistItems.push({
        id: 'hasUpperAndLowerCaseAndNumberRequirement',
        displayText: Auth0Localizer.getLocalizedText('password-policy-character-types'),
        passwordMeetsRequirement: false,
        test: (newPassword) => {
          return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/.test(newPassword);
        },
      });
    }
    if (this._passwordPolicy.hasSpecialCharacterRequirement) {
      this._checklistItems.push({
        id: 'hasSpecialCharacterRequirement',
        displayText: `${Auth0Localizer.getLocalizedText('password-policy-special-characters')} (${SPECIAL_CHARACTERS})`,
        passwordMeetsRequirement: false,
        test: (newPassword) => {
          return /^(?=.*[!@#$%^&*]).+$/.test(newPassword);
        },
      });
    }
    if (this._passwordPolicy.hasNoMoreThanTwoIdenticalCharsInARowRequirement) {
      this._checklistItems.push({
        id: 'hasNoMoreThanTwoIdenticalCharsInARowRequirement',
        displayText: Auth0Localizer.getLocalizedText('password-policy-repeating-characters'),
        passwordMeetsRequirement: false,
        test: (newPassword) => {
          // matching here is a failure
          return newPassword && !(/(.)\1{2,}/.test(newPassword));
        },
      });
    }
  };

  get passwordMeetsAllRequirements() {
    return this._checklistItems.filter(i => i.passwordMeetsRequirement).length === this._checklistItems.length;
  }

  update = (updatedPassword) => {
    this._currentPassword = updatedPassword;
    this._checklistItems.forEach(i => i.passwordMeetsRequirement = i.test(this._currentPassword));
    return this.iconUpdates;
  };

  get html() {
    const htmlTemplate = `
      <div class="resp-table" id="password-policy-table">
        <div id="policy-list-helper-text">
          ${Auth0Localizer.getLocalizedText('password-policy-list-helper-text')}
        </div>
        <div id="policy-list">
          {rows}
        </div>
      </div>`;
    const rows = this._checklistItems.map(i => this._generateItemHtmlFragment(i));
    const rowsHtml = rows.join('\n');
    return htmlTemplate.replace('{rows}', rowsHtml);
  }

  get iconUpdates() {
    return this._checklistItems.map(i => this._generateIconUpdate(i));
  }

  _generateIconTag = (item) => {
    return `<img id="${item.id}-icon" class="valid-icon" src="${item.passwordMeetsRequirement ? this._passwordValidIconUrl : this._passwordInvalidIconUrl}" />`;
  };

  _generateIconUpdate = (item) => {
    return { id: `${item.id}-icon`, iconUrl: item.passwordMeetsRequirement ? this._passwordValidIconUrl : this._passwordInvalidIconUrl };
  };

  _generateItemHtmlFragment = (item) => {
    return `
      <div class="policy-row" id="${item.id}">
        <div class="checklist-icon">${this._generateIconTag(item)}</div>
        <div class="checklist-text"><span>${item.displayText}</span></div>
      </div>`;
  };
}

class Auth0ChangePasswordPageHelper extends Auth0PageBase {
  constructor(options) {
    super(options);
    console.log(`Auth0ChangePasswordPageHelper => ${JSON.stringify(options)}`);
    this._hash = Auth0UtilsBrowser.parseUrlHash();

    // Merge white label from all available sources (priority: constructor options > session_token > wl hash param)
    // wl= is appended to the reset URL by SSAS for both user-initiated and force-change flows
    const hashWl = this._hash?.get('wl');
    const hashWhiteLabel = hashWl ? (() => { try { return JSON.parse(decodeURIComponent(hashWl)); } catch { return null; } })() : null;
    // session_token JWT also carries whiteLabelOptionsJson (set by ForceChangePasswordController)
    const sessionTokenWhiteLabel = this._sessionTokenData?.whiteLabelOptionsJson
      ? (() => { try { return JSON.parse(this._sessionTokenData.whiteLabelOptionsJson); } catch { return null; } })()
      : null;
    const mergedWhiteLabel = { ...(hashWhiteLabel || {}), ...(sessionTokenWhiteLabel || {}), ...(options.whiteLabelOptions || {}) };
    if (Object.keys(mergedWhiteLabel).length > 0) {
      this._whiteLabelOptions = mergedWhiteLabel;
      this._whiteLabelHelper = new Auth0WhiteLabelAssetHelper({
        tenantOrDomain: options.tenantOrDomain || options.tenant,
        ...this._whiteLabelOptions,
      });
    }

    this._displayMode = options.mode || Auth0Constants.CHANGE_PASSWORD_DISPLAY_MODE_INVOKE;
    this.isVoluntary = options.isVoluntary || false;
    if (this._displayMode === Auth0Constants.CHANGE_PASSWORD_DISPLAY_MODE_INVOKE && options.autoInvoke) {
      this._displayMode = Auth0Constants.CHANGE_PASSWORD_DISPLAY_MODE_AUTO_INVOKE;
    }

    // In CHANGE mode (force change password), clientId may come from continue_token JWT
    // Extract from hash if not provided in options
    this._clientId = options.clientId || this._extractClientIdFromContinueToken();

    if (this._displayMode !== Auth0Constants.CHANGE_PASSWORD_DISPLAY_MODE_CHANGE && !this._clientId) {
      throw new Error('Unable to initialize change password flow. Client Id is missing');
    }
    const qsPrefill = options.usernameOrEmailPrefill;
    this._usernamePreFill = qsPrefill
      ? decodeURIComponent(qsPrefill)
      : options.usernamePreFill;

    // CHANGE PASSWORD MODE
    this._ticket = options.ticket || this._qs.get('ticket');
    this._csrf_token = options._csrf;
    this._email = this._normalizeUsername(options.email);

    if (this._displayMode === Auth0Constants.CHANGE_PASSWORD_DISPLAY_MODE_CHANGE
        && (!this._ticket || !this._email || !this._csrf_token)) {
      this._displayMode = Auth0Constants.CHANGE_PASSWORD_DISPLAY_MODE_INVOKE;
      this._loadError = Auth0Localizer.getLocalizedText('error-change-password-load');
    }

    this._pageStyles = [
      `${Auth0Cdn.AUTH0_STYLES_URL}/auth0-change-password-styles.css?cb=71c5d67`,
    ];
    this._passwordPolicyRendered = false;
    this._stateNewPasswordValid = false;
    this._stateConfirmPasswordMatches = false;

    console.log('starting in displayMode =>', this._displayMode);
  }

  _buildPasswordResetSegmentContext = () => {
    return AuthAnalytics.buildPasswordResetContext({
      step: STEP_CHANGE_PASSWORD_PAGE,
      client_id: this._clientId,
      tenant: this.config?.auth0Tenant,
      username_or_email: this._normalizeUsername(this._email),
    });
  };

  _trackPasswordResetEvent = (eventName, additionalProperties = {}) => {
    AuthAnalytics.track(eventName, additionalProperties, this._buildPasswordResetSegmentContext());
  };

  _buildPasswordInvokeResetSegmentContext = () => {
    return AuthAnalytics.buildPasswordResetContext({
      step: STEP_START_CHANGE_PASSWORD_PAGE,
      client_id: this._clientId,
      tenant: this.config?.auth0Tenant,
      username_or_email: this._usernameOrEmailAddress,
    });
  };

  _trackPasswordInvokeResetEvent = (eventName, additionalProperties = {}) => {
    AuthAnalytics.track(eventName, additionalProperties, this._buildPasswordInvokeResetSegmentContext());
  };

  _registerInvokePasswordResetAnalytics = () => {
    // Email/username field text entry (also checks on button click to handle autofill + immediate click)
    AuthAnalytics.registerTextEntryTracking(this._invokeForEmailAddressInputElement, () => {
      this._trackPasswordInvokeResetEvent(EVENT_CHANGE_PASSWORD_EMAIL_ENTERED);
    }, { alsoCheckOn: this._invokeChangePasswordButton });

    // Invoke password reset button click
    AuthAnalytics.registerButtonClickEvent(this._invokeChangePasswordButton, () => {
      this._trackPasswordInvokeResetEvent(EVENT_CHANGE_PASSWORD_FLOW_STARTED);
    });

    // Cancel link tracking (EVENT_CHANGE_PASSWORD_CANCEL)
    // Uses inline await pattern before navigation in _cancelChangePassword() handler
  };

  _registerChangePasswordAnalytics = () => {
    // New password field text entry (also checks on button click to handle autofill + immediate click)
    AuthAnalytics.registerTextEntryTracking(this._newPasswordInputElement, () => {
      this._trackPasswordResetEvent(EVENT_CHANGE_PASSWORD_NEW_PASSWORD_ENTERED);
    }, { alsoCheckOn: this._changePasswordButton });

    // Confirm password field text entry (also checks on button click to handle autofill + immediate click)
    AuthAnalytics.registerTextEntryTracking(this._confirmNewPasswordInputElement, () => {
      this._trackPasswordResetEvent(EVENT_CHANGE_PASSWORD_CONFIRMATION_ENTERED);
    }, { alsoCheckOn: this._changePasswordButton });

    // Change password button click
    AuthAnalytics.registerButtonClickEvent(this._changePasswordButton, () => {
      this._trackPasswordResetEvent(EVENT_CHANGE_PASSWORD_BUTTON_CLICKED);
    });

    // Cancel link tracking (EVENT_CHANGE_PASSWORD_CANCEL)
    // Uses inline await pattern before navigation in _cancelChangePassword() handler
  };

  _normalizeUsername = (maybeEncodedUsername) => {
    // Auth0 REQUIRES that we use an email address for lookup and it will shutdown the whole API call if we try to pass a non email string
    // as the identifier. Because Auth0 tracks internally what address we started this flow for and we gave it this fake email address
    // (IdS knows to discard it on lookup), Auth0 will give us the email address back for display here. Unfortunately, it hands us back the
    // same fake email that we registered the ticket for. So... we need to strip it out here.
    if (!maybeEncodedUsername) return maybeEncodedUsername;
    return Auth0UtilsBrowser.decodeUsernameFromEncodedEmail(maybeEncodedUsername);
  };

  get _isUsernameOnlyMode() { return true; }

  get _invokeForEmailAddressInputElement() { return document.getElementById('change-password-for-email'); }

  get _usernameOrEmailAddress() {
    if (this._invokeForEmailAddressInputElement) {
      // we are in manual mode so return form value
      return this._invokeForEmailAddressInputElement.value;
    }
    // we are in auto invoke mode to fetch from qs
    return this._usernamePreFill;
  }

  get _usernameOrEmailMasked() {
    return this._maskUsernameOrEmail(this._usernameOrEmailAddress);
  }

  _maskUsernameOrEmail = (usernameOrEmailAddress) => {
    return this._testIsEmailAddress(usernameOrEmailAddress)
      ? Auth0UtilsBrowser.getMaskedEmailAddressDisplayString(usernameOrEmailAddress)
      : Auth0UtilsBrowser.getMaskedUsernameDisplayString(usernameOrEmailAddress);
  };

  get _isEmailAddress() {
    // The AutoInvoke UI does not allow for a user to input a username/email so we rely on the auth0 provided value
    const shouldTestAutoInvokeValue = this._isAutoInvokeMode;
    const value = shouldTestAutoInvokeValue ? this._usernamePreFill : this._usernameOrEmailAddress;
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);
  }

  _testIsEmailAddress = (value) => {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);
  };

  get _invokeChangePasswordButton() { return document.getElementById('btn-change-password-for-email'); }

  get _autoInvokeResendChangePasswordEmailButton() { return document.getElementById('btn-resend-email'); }

  get _newPasswordInputElement() { return document.getElementById('new-password'); }

  get _newPassword() { return this._newPasswordInputElement.value; }

  get _confirmNewPasswordInputElement() { return document.getElementById('confirm-new-password'); }

  get _confirmNewPassword() { return this._confirmNewPasswordInputElement.value; }

  get _newPasswordRevealIconElement() { return document.getElementById('new-password-visibility-icon'); }

  get _confirmNewPasswordRevealIconElement() { return document.getElementById('confirm-new-password-visibility-icon'); }

  get _newPasswordValidIconElement() { return document.getElementById('new-password-valid-icon'); }

  get _confirmNewPasswordValidIconElement() { return document.getElementById('confirm-new-password-valid-icon'); }

  get _changeEmailAddressInputValidIconElement() { return document.getElementById('change-password-for-email-valid-icon'); }

  get _passwordPolicyContainer() { return document.getElementById('password-policy-container'); }

  get _changePasswordButton() { return document.getElementById('btn-change-password'); }

  get _passwordPolicy() { return this.__passwordPolicy; }

  get _continueToken() { return this._hash.get('continue_token'); } // only exists in force change password flow

  // session_token is the JWT issued by Auth0 action (ForceChangePasswordController/AdaptiveMfaController)
  // Contains: sessionId, triggeredCampaignIds, username, startTimestamp, whiteLabelOptionsJson, continueUrl, appLoginUrl
  get _sessionToken() { return this._qs.get('session_token'); }

  get _sessionTokenData() {
    if (!this._sessionToken) return null;
    try {
      return Auth0UtilsBrowser.getJwtBodyFromToken(this._sessionToken);
    } catch {
      return null;
    }
  }

  get _continueUrl() {
    return this._continueToken ? Auth0UtilsBrowser.getJwtBodyFromToken(this._continueToken)?.continueUrl : null;
  }

  get _continueUsername() {
    return this._continueToken ? Auth0UtilsBrowser.getJwtBodyFromToken(this._continueToken)?.username : null;
  }

  /**
   * Extract clientId from continue_token JWT (used in force change password flow)
   * @returns {string|null} Client ID or null
   */
  _extractClientIdFromContinueToken() {
    const continueToken = this._hash?.get('continue_token');
    if (!continueToken) return null;
    try {
      const payload = Auth0UtilsBrowser.getJwtBodyFromToken(continueToken);
      return payload?.clientId || null;
    } catch {
      return null;
    }
  }

  get _continueState() {
    if (this.__continueState) return this.__continueState;
    this.__continueState = Auth0UtilsBrowser.getOption(AUTH0_CONTINUE_STATE_KEY);
    return this.__continueState;
  }

  get _appLoginUrl() {
    // Priority 1: Check session_token JWT for appLoginUrl (set by Auth0 action from original app)
    // This is the cleanest approach - the URL travels with the auth flow via the JWT
    const sessionTokenAppLoginUrl = this._sessionTokenData?.appLoginUrl;
    if (sessionTokenAppLoginUrl) {
      console.log('[_appLoginUrl] Using appLoginUrl from session_token =>', sessionTokenAppLoginUrl);
      return sessionTokenAppLoginUrl;
    }

    // Priority 2: Check hash fragment for appLoginUrl (from email link - Auth0 puts params after #)
    // The email template includes appLoginUrl, and Auth0's /lo/reset endpoint puts it in the hash
    const hashAppLoginUrl = this._hash.get('appLoginUrl');
    if (hashAppLoginUrl) {
      console.log('[_appLoginUrl] Using appLoginUrl from hash =>', hashAppLoginUrl);
      return hashAppLoginUrl;
    }

    // Priority 3: Check query string for appLoginUrl (fallback if not in hash)
    const qsAppLoginUrl = this._qs.get('appLoginUrl');
    if (qsAppLoginUrl) {
      console.log('[_appLoginUrl] Using appLoginUrl from query string =>', qsAppLoginUrl);
      return qsAppLoginUrl;
    }

    // Priority 4: Use appLoginUrl from options (passed by pre-login handler)
    if (this.__appLoginUrl && this.__appLoginUrl !== window.location.origin) {
      console.log('[_appLoginUrl] Using __appLoginUrl from options =>', this.__appLoginUrl);
      return this.__appLoginUrl;
    }

    // Priority 5: Check for continue_url in hash/query or session redirect URI
    const baseUrl = this._hash.get('continue_url') || this._qs.get('continue_url') || Auth0UserSessionHelper.auth0SpaJsSessionRedirectUri || window.location.origin;

    // When falling back to origin, append targetClientId for tenant context
    // This ensures Cancel navigation preserves tenant context for SSAS
    if (baseUrl === window.location.origin) {
      const targetClientId = this._qs.get('targetClientId') || this._clientId;
      if (targetClientId) {
        const fallbackUrl = `${baseUrl}/loginauth?targetClientId=${encodeURIComponent(targetClientId)}`;
        console.log('[_appLoginUrl] Using fallback with targetClientId =>', fallbackUrl);
        return fallbackUrl;
      }
    }

    console.log('[_appLoginUrl] Returning baseUrl =>', baseUrl);
    return baseUrl;
  }

  _persistContinueLoginState = () => {
    const continueState = this._qs.get('state');
    console.log(`_persistContinueLoginState => ${continueState}`);
    Auth0UtilsBrowser.setOption({
      cookieName: AUTH0_CONTINUE_STATE_KEY,
      value: continueState,
      expirySec: 600,
    });
  };

  get _passwordValidIconUrl() {
    return `${Auth0Cdn.AUTH0_IMAGES_URL}/${Auth0Constants.PASSWORD_VALID_ICON}?cb=${this._scriptLoader._getScriptCacheKiller()}`;
  }

  get _passwordInvalidIconUrl() {
    return `${Auth0Cdn.AUTH0_IMAGES_URL}/${Auth0Constants.PASSWORD_INVALID_ICON}?cb=${this._scriptLoader._getScriptCacheKiller()}`;
  }

  get _revealPasswordIconUrl() {
    return `${Auth0Cdn.AUTH0_IMAGES_URL}/${Auth0Constants.SHOW_PASSWORD_ICON}?cb=${this._scriptLoader._getScriptCacheKiller()}`;
  }

  get _hidePasswordIconUrl() {
    return `${Auth0Cdn.AUTH0_IMAGES_URL}/${Auth0Constants.HIDE_PASSWORD_ICON}?cb=${this._scriptLoader._getScriptCacheKiller()}`;
  }

  get _isAutoInvokeMode() { return this._displayMode === Auth0Constants.CHANGE_PASSWORD_DISPLAY_MODE_AUTO_INVOKE; }

  get _isInvokeMode() { return !this._displayMode || this._displayMode === Auth0Constants.CHANGE_PASSWORD_DISPLAY_MODE_INVOKE; }

  get _isChangeMode() { return this._displayMode === Auth0Constants.CHANGE_PASSWORD_DISPLAY_MODE_CHANGE; }

  _setPasswordPolicy = async (persist = false) => {
    try {
      console.log('_fetchPasswordPolicy start');
      const persisted = Auth0UtilsBrowser.getOption(Auth0Constants.PASSWORD_POLICY_KEY, false, true);
      console.log('_fetchPasswordPolicy found (local) =>', persisted);
      if (!persist && persisted) {
        console.log('_fetchPasswordPolicy success (local) =>', persisted);
        this._setLocalPolicyStateFromRemoteState(persisted);
        return;
      }

      const policy = await this._auth0ManagementApi.getPasswordPolicy();
      console.log(`_fetchPasswordPolicy success (remote) => ${JSON.stringify(policy)}`);

      if (persist) {
        Auth0UtilsBrowser.setOption({
          cookieName: Auth0Constants.PASSWORD_POLICY_KEY,
          value: policy,
          stringify: true,
          expirySec: 600,
        });
        return;
      }

      // otherwise just set state with it
      this._setLocalPolicyStateFromRemoteState(policy);
    } catch (error) {
      console.log(`_fetchPasswordPolicy error => ${Auth0UtilsBrowser.errorToString(error)}`);
      if (!persist) {
        this._setLocalPolicyStateFromRemoteState({});
        this._passwordPolicyName = 'none';
        this._displayError('error-fetch-password-policy');
      } else {
        logger.warn(`Failed to fetch password policy => ${JSON.stringify(error)}`);
      }
    }
  };

  _setLocalPolicyStateFromRemoteState = (remoteState) => {
    this._passwordPolicyName = remoteState.passwordPolicy;
    this._passwordComplexity = remoteState.passwordComplexityOptions;
    this._passwordMinLength = remoteState.passwordComplexityOptions?.min_length || 1;
    this._passwordHistoryRestrictionObject = remoteState.passwordHistory;
    this._passwordHistoryRestriction = remoteState.passwordHistory?.size || 5;
    this._passwordPolicyDictionary = remoteState.passwordDictionary;
    this._passwordPolicyNoPersonalInfo = remoteState.passwordNoPersonalInfo;

    this.__passwordPolicy = PASSWORD_REQUIREMENTS_MAP[this._passwordPolicyName];
  };

  _buildRequestChangePasswordBody = () => {
    const flowType = this._isAutoInvokeMode ? 'force-change' : 'user-initiated';
    console.log('[ChangePasswordPage] Building request body:', { flowType, appLoginUrl: this._appLoginUrl });
    return {
      username: this._usernameOrEmailAddress,
      // client_id is used by SSAS tenant middleware to resolve correct tenant class
      client_id: this._clientId,
      // redirectUrl: The app's login URL for post-change-password redirect (user-initiated only)
      redirectUrl: this._appLoginUrl,
      flowType,
      ...(this._whiteLabelOptions && Object.keys(this._whiteLabelOptions).length > 0 && {
        whiteLabelOptionsJson: JSON.stringify(this._whiteLabelOptions), // codeql[js/bad-code-sanitization] - value is sent as HTTP POST body field, not injected into script context
      }),
    };
  };

  _invokeChangePasswordHandler = async (event) => {
    console.log('_invokeChangePasswordHandler start');
    event?.preventDefault();

    this._disableButton(this._invokeChangePasswordButton, 'please-wait-generic');

    try {
      const invokePasswordChangeBody = this._buildRequestChangePasswordBody();
      console.log(`_invokeChangePasswordHandler request => ${JSON.stringify(invokePasswordChangeBody)}`);
      const invokeResponse = await this._auth0ManagementApi.startRequestChangePasswordFlow(invokePasswordChangeBody);
      console.log(`_invokeChangePasswordHandler response => ${JSON.stringify(invokeResponse)}`);
      this._invokePasswordChangeSuccess();
      // just take advantage of natural delay for user to go find email code
      this._setPasswordPolicy(true);
    } catch (error) {
      console.log(`_invokeChangePasswordHandler ERROR => ${Auth0UtilsBrowser.errorToString(error)}`);
      console.log(`_invokeChangePasswordHandler ERROR => ${JSON.stringify(error.data)}`);
      this._enableButton(this._invokeChangePasswordButton, 'btn-change-password-for-email');

      let errorKey = 'change-password-invoked-fail';
      const errorDataMessage = error.response?.dataObj?.message;
      if (errorDataMessage) {
        const localizationKey = Auth0Localizer.resourceKeyFromMessage(errorDataMessage);
        if (localizationKey !== errorDataMessage) {
          errorKey = localizationKey;
        }
        // if generic ambiguous user error, swap it with a change password specific message for clarity
        errorKey = errorKey === 'error-ambiguous-user-generic' ? 'error-change-password-ambiguous-user' : errorKey;
      }
      this._displayError(errorKey, { supportEmail: this._whiteLabelHelper.appLoginSupportEmail, supportPhoneNumber: this._whiteLabelHelper.appLoginSupportPhoneNumber });
    }
  };

  _autoInvokeResendChangePasswordHandler = async (event) => {
    event?.preventDefault();

    this._hideElement(document.getElementById('auto-invoke-email-nondelivery-prompt'));
    this._disableButton(this._autoInvokeResendChangePasswordEmailButton, 'btn-resending-in-progress');

    try {
      const invokePasswordChangeBody = this._buildRequestChangePasswordBody();
      console.log(`_autoInvokeResendChangePasswordHandler request => ${JSON.stringify(invokePasswordChangeBody)}`);
      const autoInvokeResponse = await this._auth0ManagementApi.startRequestChangePasswordFlow(invokePasswordChangeBody);
      console.log(`_autoInvokeResendChangePasswordHandler response => ${JSON.stringify(autoInvokeResponse)}`);
      this._autoInvokePasswordChangeSuccess();
      // just take advantage of natural delay for user to go find email code
      this._setPasswordPolicy(true);
    } catch (error) {
      this._showElement(document.getElementById('auto-invoke-email-nondelivery-prompt'));
      console.log(`_autoInvokeResendChangePasswordHandler ERROR => ${Auth0UtilsBrowser.errorToString(error)}`);
      this._displayError('change-password-invoked-fail', { supportEmail: this._whiteLabelHelper.appLoginSupportEmail, supportPhoneNumber: this._whiteLabelHelper.appLoginSupportPhoneNumber });
    }
  };

  _invokePasswordChangeSuccess = () => {
    const messageKey = !this._isUsernameOnlyMode && this._isEmailAddress ? 'change-password-invoked-success-email' : 'change-password-invoked-success-username';
    this._displayInfo(messageKey, { usernameOrEmail: this._usernameOrEmailMasked, supportEmail: this._whiteLabelHelper.appLoginSupportEmail });
    this._disableButton(this._invokeChangePasswordButton, 'check-your-email');

    this._persistContinueLoginState();
  };

  _autoInvokePasswordChangeSuccess = () => {
    this._autoInvokeResendChangePasswordEmailButton.innerHTML = `
      <img id="resent-email-icon" class="valid-icon" src="${Auth0Cdn.AUTH0_IMAGES_URL}/${Auth0Constants.PASSWORD_VALID_ICON}" />
    `;

    this._persistContinueLoginState();
  };

  get _changePasswordPostArgs() {
    const args = new URLSearchParams({
      _csrf: this._csrf_token,
      ticket: this._ticket,
      email: this._email,
      newPassword: this._newPassword,
      confirmNewPassword: this._confirmNewPassword,
    }).toString();
    console.log('_postArgs =>', args);
    return args;
  }

  _changePasswordHandler = async (event) => {
    console.log('_changePasswordHandler start');
    event?.preventDefault();

    this._disableButton(this._changePasswordButton, 'please-wait-generic');
    try {
      // Use the current origin since this page is served from the custom domain
      const changePasswordUrl = `${window.location.origin}/lo/reset`;
      console.log('_changePasswordHandler =>', changePasswordUrl);
      await Auth0HttpHelper.postData({
        url: changePasswordUrl,
        body: this._changePasswordPostArgs,
        contextKey: 'error-change-password',
      });
      this._passwordChangeSuccess();
    } catch (error) {
      console.log(`_changePasswordHandler ERROR => ${JSON.stringify(error)}`);

      // Track password change failure
      this._trackPasswordResetEvent(EVENT_CHANGE_PASSWORD_FAILED, {
        error_message: error?.message || 'Unknown error',
        error_code: error?.code || error?.statusCode,
      });

      this._passwordChangeFail(error);
    }
  };

  _passwordChangeSuccess = async () => {
    console.log('_passwordChangeSuccess start');
    console.log('continue url =>', this._continueUrl);
    console.log('continue token =>', this._continueToken);
    console.log('hash =>', this._hash.toString());
    this._displayInfo('password-changed-success-continue');
    this._disableButton(this._changePasswordButton, 'continuing-login-wait');

    // Track password change success before navigation
    if (this._isChangeMode) {
      await this._trackPasswordResetEvent(EVENT_CHANGE_PASSWORD_SUCCESS);
    } else {
      await this._trackPasswordInvokeResetEvent(EVENT_CHANGE_PASSWORD_SUCCESS);
    }

    this._continueToAppHandler();
  };

  _continueToAppHandler = () => {
    console.log('_continueToAppHandler start');
    if (this._alreadyContinuing) return;
    this._alreadyContinuing = true;

    // Always redirect to the app's login page after password change.
    // For force-change flows, appLoginUrl is now included in the email link alongside the
    // continue_token, so the app's login page receives the user and initiates a fresh login
    // which naturally picks up the full app config (WL, SSO, etc.) from the consuming app.
    const appLoginUrl = this._appLoginUrl;
    console.log('[_continueToAppHandler] Redirecting to app login URL:', appLoginUrl);
    this._writeMessageForNextLoginPageRender('password-changed-success-login-again');
    window.location.href = appLoginUrl;
  };

  _passwordChangeFail = (error) => {
    const errMessageData = this._mapResponseError(error);
    // reset submit button (presumably any error here is correctable)
    this._enableButton(this._changePasswordButton, 'btn-change-password');
    this._displayError(errMessageData.key, {
      ...errMessageData.replacements,
      supportEmail: this._whiteLabelHelper.appLoginSupportEmail,
      supportPhoneNumber: this._whiteLabelHelper.appLoginSupportPhoneNumber,
    });
  };

  _mapResponseError = (error) => {
    const responseBodyObj = error.response?.dataObj || {};

    const getDefaultError = () => {
      return { key: 'error-change-password', replacements: {} };
    };

    const getAuth0ErrorKey = () => {
      const passwordErrorsMap = {
        PasswordStrengthError: { key: 'error-change-password-too-weak', replacements: { } },
        PasswordHistoryError: { key: 'error-change-password-history', replacements: { historyNum: this._passwordHistoryRestriction } },
        PasswordDictionaryError: { key: 'error-change-password-dictionary', replacements: { } },
        PasswordNoUserInfoError: { key: 'error-change-password-no-user-info', replacements: { } },
        PasswordBreachedError: { key: 'error-change-password-breached-password', replacements: { } },
      };

      console.log('_mapResponseError lookup for =>', responseBodyObj.name);
      const auth0ErrorType = passwordErrorsMap[responseBodyObj.name];
      if (auth0ErrorType) {
        console.log(`_mapResponseError lookup for ${responseBodyObj.name} =>`, JSON.stringify(auth0ErrorType));
        return auth0ErrorType;
      }

      // Check for error message from IdS/backend response
      const directIdsFailureResponse = error.response?.dataObj?.message;
      console.log('_mapResponseError directIdsFailureResponse =>', directIdsFailureResponse);

      if (directIdsFailureResponse) {
        // Try to find a localization key for this message
        const derivedKey = Auth0Localizer.resourceKeyFromMessage(directIdsFailureResponse);
        console.log('_mapResponseError derivedKey =>', derivedKey);

        // Check if derivedKey is a valid localization key (not just the original message echoed back)
        if (Auth0Localizer.isValidLocalizationKey(derivedKey)) {
          // Special case: password history error needs the historyNum replacement
          if (derivedKey === 'error-change-password-history') {
            return { key: derivedKey, replacements: { historyNum: this._passwordHistoryRestriction } };
          }
          return { key: derivedKey, replacements: {} };
        }

        // No localization key found - use the raw message from the backend as a fallback
        // This ensures users see something meaningful like "No such user" instead of "undefined"
        console.log('_mapResponseError using raw backend message =>', directIdsFailureResponse);
        return { key: directIdsFailureResponse, replacements: {}, isRawMessage: true };
      }

      return null;
    };

    try {
      return getAuth0ErrorKey() || getDefaultError();
    } catch (mappingError) {
      console.log('_mapResponseError mapping error =>', mappingError);
    }

    return getDefaultError();
  };

  _revealPasswordHandler = () => {
    const cb = this._scriptLoader._getScriptCacheKiller();
    const currentType = this._newPasswordInputElement.getAttribute('type');

    const getNextType = () => {
      if (currentType === 'password') {
        return 'text';
      }
      return 'password';
    };

    const getNextIcon = () => {
      if (currentType === 'password') {
        return Auth0Constants.SHOW_PASSWORD_ICON;
      }
      return Auth0Constants.HIDE_PASSWORD_ICON;
    };

    const toggleInputType = () => {
      const nextType = getNextType();
      this._newPasswordInputElement.setAttribute('type', nextType);
      this._confirmNewPasswordInputElement.setAttribute('type', nextType);
    };

    const toggleIcon = () => {
      const nextIcon = getNextIcon();
      this._newPasswordRevealIconElement.src = `${Auth0Cdn.AUTH0_IMAGES_URL}/${nextIcon}?cb=${cb}`;
      this._confirmNewPasswordRevealIconElement.src = this._newPasswordRevealIconElement.src;
    };

    toggleInputType();
    toggleIcon();
  };

  _shouldChangePasswordButtonBeEnabled = () => {
    return this._stateNewPasswordValid && this._stateConfirmPasswordMatches;
  };

  _updateChangePasswordButtonEnabled = () => {
    this._enableButton(this._changePasswordButton, 'btn-change-password', this._shouldChangePasswordButtonBeEnabled());
  };

  _newPasswordChanged = () => {
    // implement policy compatibility inspection here...
    this._updatePasswordPolicyChecklistTable(this._newPasswordInputElement.value);
    this._updateChangePasswordButtonEnabled();
    this._validateConfirmationPassword();
    this._updateChangePasswordButtonEnabled();
  };

  _confirmNewPasswordChanged = () => {
    // implement matching checks here
    this._validateConfirmationPassword();
    this._updateChangePasswordButtonEnabled();
  };

  _invokeChangePasswordUsernameChanged = () => {
    // implement matching checks here
    const isUsernameOrEmailOk = this._usernameOrEmailAddress.length >= 2;
    this._enableButton(this._invokeChangePasswordButton, 'btn-change-password-for-email', isUsernameOrEmailOk);
    this._updateValidEmailAddressIcon(isUsernameOrEmailOk);
  };

  _updateValidEmailAddressIcon = (usernameOrEmailIsValid) => {
    const shouldBeUrl = usernameOrEmailIsValid ? this._passwordValidIconUrl : this._passwordInvalidIconUrl;
    if (this._changeEmailAddressInputValidIconElement.src !== shouldBeUrl) {
      this._changeEmailAddressInputValidIconElement.src = shouldBeUrl;
    }
  };

  _validateConfirmationPassword = () => {
    const newPwd = this._newPassword;
    const confirmPwd = this._confirmNewPassword;

    let shouldBeUrl;
    if (newPwd && confirmPwd && newPwd === confirmPwd) {
      shouldBeUrl = this._passwordValidIconUrl;
      this._confirmNewPasswordInputElement.style.borderColor = COLOR_VALID_FIELD;
      this._stateConfirmPasswordMatches = true;
    } else {
      shouldBeUrl = this._passwordInvalidIconUrl;
      if (this._confirmNewPasswordInputElement.value) this._confirmNewPasswordInputElement.style.borderColor = COLOR_INVALID_FIELD;
      this._stateConfirmPasswordMatches = false;
    }

    if (this._confirmNewPasswordValidIconElement.src !== shouldBeUrl) {
      this._confirmNewPasswordValidIconElement.src = shouldBeUrl;
    }
  };

  _buildPasswordPolicyChecklist = () => {
    if (this._passwordPolicyName === 'none') return;

    this._passwordPolicyChecklist = new Auth0PasswordPolicyChecklist({
      passwordPolicyName: this._passwordPolicyName,
      passwordMinLength: this._passwordMinLength,
    });
  };

  _updatePasswordPolicyChecklistTable = (newPassword = '') => {
    if (!this._passwordPolicyChecklist) return;

    if (!this._passwordPolicyRendered) {
      this._passwordPolicyRendered = true;
      this._passwordPolicyChecklist.update(newPassword);
      this._passwordPolicyContainer.innerHTML = this._passwordPolicyChecklist.html;
    } else {
      // just update image icons
      const iconUpdates = this._passwordPolicyChecklist.update(newPassword);
      iconUpdates.forEach((i) => {
        const ele = document.getElementById(i.id);
        if (ele.src !== i.iconUrl) {
          ele.src = i.iconUrl;
        }
      });
    }

    let shouldBeUrl;
    if (this._passwordPolicyChecklist.passwordMeetsAllRequirements) {
      shouldBeUrl = this._passwordValidIconUrl;
      this._newPasswordInputElement.style.borderColor = COLOR_VALID_FIELD;
      this._stateNewPasswordValid = true;
    } else {
      shouldBeUrl = this._passwordInvalidIconUrl;
      if (this._newPasswordInputElement.value) this._newPasswordInputElement.style.borderColor = COLOR_INVALID_FIELD;
      this._stateNewPasswordValid = false;
    }
    if (this._newPasswordValidIconElement.src !== shouldBeUrl) {
      this._newPasswordValidIconElement.src = shouldBeUrl;
    }
  };

  _localize = async () => {
    await super._localize();

    if (this._isChangeMode) {
      Auth0Localizer.setElementLocalizedText('change-password-text');
      Auth0Localizer.setElementLocalizedText('btn-change-password');
      Auth0Localizer.setElementLocalizedText('new-password-label');
      Auth0Localizer.setElementLocalizedText('confirm-new-password-label');
      Auth0Localizer.setElementLocalizedText('change-password-footer', {
        // TODO: DAT - figure out the richTextElements so we can pass in HTML elements
        // supportEmailLink: '<a href="mailto:loginsupport@shipstation.com>loginsupport@shipstation.com</a>',
        supportEmailLink: this._whiteLabelHelper.appLoginSupportEmail,
      });
    } else if (this._isAutoInvokeMode) {
      Auth0Localizer.setElementLocalizedText('auto-invoke-change-password-title');
      Auth0Localizer.setElementLocalizedText('auto-invoke-change-password-explainer-text');
      Auth0Localizer.setElementLocalizedText('auto-invoke-change-password-email-acknowledgment');
      Auth0Localizer.setElementLocalizedText('auto-invoke-change-password-username-acknowledgment');
      Auth0Localizer.setElementLocalizedText('auto-invoke-email-duration-disclaimer');
      Auth0Localizer.setElementLocalizedText('auto-invoke-email-nondelivery-prompt');
      Auth0Localizer.setElementLocalizedText('btn-resend-email');
      Auth0Localizer.setElementLocalizedText('footer-support-contact-info', {
        // TODO: DAT - figure out the richTextElements so we can pass in HTML elements
        // supportEmailLink: '<a href="mailto:loginsupport@shipstation.com>loginsupport@shipstation.com</a>',
        supportEmailLink: this._whiteLabelHelper.appLoginSupportEmail,
        supportPhoneNumber: this._whiteLabelHelper.appLoginSupportPhoneNumber,
      });
    } else { // invoke change password mode
      Auth0Localizer.setElementLocalizedText('invoke-change-password-text');
      Auth0Localizer.setElementLocalizedText('btn-change-password-for-email');
      this._applyInvokeModeDynamicLocalizations(true); // TODO: DAT - get this in
    }
  };

  _applyInvokeModeDynamicLocalizations = () => {
    const labelElementId = 'change-password-for-email-label';
    const labelText = this._isUsernameOnlyMode ? 'change-password-for-username-only-label' : 'change-password-for-email-label';
    Auth0Localizer.setElementLocalizedText({ id: labelElementId, resourceIdOverride: labelText });
    if (this._isUsernameOnlyMode) {
      Auth0Localizer.setElementLocalizedText('change-password-for-username-only-helper-text');
    }
  };

  _initChangePasswordHtmlForChangeMode = () => {
    console.log('_initChangePasswordHtmlForChangeMode start');
    this._buildPasswordPolicyChecklist();

    this._newPasswordInputElement.focus();
    this._newPasswordRevealIconElement.src = this._hidePasswordIconUrl;
    this._confirmNewPasswordRevealIconElement.src = this._newPasswordRevealIconElement.src;

    // hidden for now, delete if we decide to not use (currently using red border on input element instead)
    this._hideElement(this._newPasswordValidIconElement);
    this._newPasswordValidIconElement.src = this._passwordInvalidIconUrl;
    // hidden for now, delete if we decide to not use (currently using red border on input element instead)
    this._hideElement(this._confirmNewPasswordValidIconElement);
    this._confirmNewPasswordValidIconElement.src = this._newPasswordValidIconElement.src;

    document.getElementById('id-card-email').innerText = this._maskUsernameOrEmail(this._continueUsername || this._email);

    // add event handlers for start link and cancel link
    document.getElementById('change-password-form-fields').addEventListener('submit', this._changePasswordHandler);
    this._newPasswordInputElement.addEventListener('input', this._newPasswordChanged);
    this._confirmNewPasswordInputElement.addEventListener('input', this._confirmNewPasswordChanged);
    window.revealPasswordHandler = this._revealPasswordHandler;

    // Register all Segment analytics tracking for change mode
    this._registerChangePasswordAnalytics();
  };

  _initChangePasswordHtmlForInvokeMode = async () => {
    console.log('_initChangePasswordHtmlForInvokeMode start');
    this._invokeForEmailAddressInputElement.value = this._usernamePreFill || '';
    this._invokeForEmailAddressInputElement.focus();
    this._invokeForEmailAddressInputElement.addEventListener('input', this._invokeChangePasswordUsernameChanged);
    this._invokeChangePasswordButton.addEventListener('click', this._invokeChangePasswordHandler);

    // If username was pre-filled (e.g., from progressive auth context), enable the button immediately
    if (this._usernamePreFill) {
      this._invokeChangePasswordUsernameChanged();
    }

    // hidden for now, delete if we decide to not use (currently using red border on input element instead)
    this._hideElement(this._changeEmailAddressInputValidIconElement);
    this._changeEmailAddressInputValidIconElement.src = this._passwordInvalidIconUrl;

    Auth0UserSessionHelper.copyAuth0SpaJsSessionStateToCookie();

    // Register all Segment analytics tracking for invoke mode
    this._registerInvokePasswordResetAnalytics();
  };

  _initChangePasswordHtmlForAutoInvokeMode = async () => {
    console.log('_initChangePasswordHtmlForAutoInvokeMode start');
    const isEmail = this._testIsEmailAddress(this._usernamePreFill);
    const userCardValue = isEmail
      ? Auth0UtilsBrowser.getMaskedEmailAddressDisplayString(this._usernamePreFill)
      : this._usernamePreFill;
    if (this._isUsernameOnlyMode || !isEmail) {
      document.getElementById('auto-invoke-change-password-email-acknowledgment').style.display = 'none';
    } else {
      document.getElementById('auto-invoke-change-password-username-acknowledgment').style.display = 'none';
    }

    document.getElementById('id-card-email').innerText = userCardValue;
    if (this.isVoluntary) {
      document.getElementById('auto-invoke-change-password-explainer-text').style.display = 'none';
    }

    this._autoInvokeResendChangePasswordEmailButton.addEventListener('click', this._autoInvokeResendChangePasswordHandler);

    Auth0UserSessionHelper.copyAuth0SpaJsSessionStateToCookie();
    await this._autoInvokeResendChangePasswordHandler();
  };

  _initChangePasswordHtml = async () => {
    console.log('_initChangePasswordHtml start');
    await this._localize();
    await this._applyWhiteLabel(this._whiteLabelOptions);

    if (this._isChangeMode) {
      await this._setPasswordPolicy();
      this._initChangePasswordHtmlForChangeMode();
      this._updatePasswordPolicyChecklistTable();
    } else if (this._isAutoInvokeMode) {
      this._initChangePasswordHtmlForAutoInvokeMode();
    } else {
      this._initChangePasswordHtmlForInvokeMode();
    }

    this._showPage();

    if (this._loadError) {
      this._displayError(this._loadError);
    }
  };

  _cancelChangePassword = async () => {
    // Track cancel event before navigation
    if (this._isChangeMode) {
      await this._trackPasswordResetEvent(EVENT_CHANGE_PASSWORD_CANCEL);
    } else {
      await this._trackPasswordInvokeResetEvent(EVENT_CHANGE_PASSWORD_CANCEL);
    }

    if (this._isAutoInvokeMode) {
      console.log('_cancelChangePassword auto invoked; full logout');
      this._logout();
      return;
    }
    console.log('_cancelChangePassword start =>', this._appLoginUrl);
    window.location.href = this._appLoginUrl;
  };

  _getBodyHtml = () => {
    if (this._isChangeMode) return ChangePasswordHtml;
    if (this._isAutoInvokeMode) return AutoInvokeChangePasswordHtml;
    return InvokeChangePasswordHtml;
  };

  async _onLoad() {
    await super._onLoad();
    // called when render of underlying template is complete

    this.toolbarVisible = true;
    this.actionLinkVisible = true;
    this.actionLinkText = Auth0Localizer.getLocalizedText('toolbar-action-cancel');
    this.actionLinkTextHandler = this._cancelChangePassword;

    await this.setBody(this._getBodyHtml());
  }

  /**
   * @function
   * @async
   * @description Directs the `Auth0LinkingPageHelper` to start the linking process. A valid SSO Identity Provider auth must have already happened before this page loads.
   */
  async _startUi() {
    await super._startUi();
    await this._initChangePasswordHtml();
  }
}

export { Auth0ChangePasswordPageHelper };
